import {dehydrate, QueryClient} from '@tanstack/react-query';
import {GetStaticPropsContext} from 'next';

import {CertificationAggregatorApi} from '@udemy/browse-aggregator';
import {
    getAllAcademiesForLocale,
    InDemandCareer,
    buildInDemandCareer,
} from '@udemy/career-academies';
import {disallowSlug, RENDER_MODES} from '@udemy/nextjs-core';
import {udSentry} from '@udemy/sentry';

import {
    CertificationAggregatorComponentData,
    fetchGenericCertificationAggregatorComponentData,
} from 'src/features/black-friday-cyber-monday-sales/lib/fetch-certification-aggregators-data';
import {getAcademyDetailsData} from 'src/features/black-friday-cyber-monday-sales/lib/get-academies-details-data';
import {getBundleCoursesAndPopularTopics} from 'src/features/black-friday-cyber-monday-sales/lib/get-bundle-courses';
import {getTopicsForBlackFridayCyberMondaySales} from 'src/features/black-friday-cyber-monday-sales/utils/constants';
import {getCompactTranslations} from 'src/lib-server/locale.server';
import {FOUR_HOURS} from 'src/lib/constants';
import {fetcher} from 'src/lib/gql-fetcher';
import {udemyApi} from 'src/lib/ud-global-fetch';
import {AppContextData} from 'src/types/page-props';

import {getLayout} from '../../components/layout';
import {
    BlackFridayLandingPageFeature,
    BlackFridayCyberMondaySalesPageFeatureProps,
} from '../../features/black-friday-cyber-monday-sales';
import {getStaticAppContext} from '../api/context.api';
import {getTopicData} from '../topic/lib/get-topic-data';

export async function getStaticPaths() {
    return {
        paths: [],
        fallback: 'blocking',
    };
}

export const getStaticProps = disallowSlug(async (context: GetStaticPropsContext) => {
    const locale = context.locale as string;

    // Common page data
    const translations = await getCompactTranslations(locale);
    const client = new QueryClient();

    // Resolve Career Academies
    const academiesListForLocale = getAllAcademiesForLocale(locale);

    const academyDetailsPromise = Promise.allSettled(
        academiesListForLocale.map((academy) =>
            getAcademyDetailsData(academy.curriculum.buyableIds.map(Number), locale),
        ),
    );

    // START OF CERTIFICATION AGGREGATORS DATA FETCHING

    // Generic aggregator data fetching (no slug required)
    let componentData: CertificationAggregatorComponentData | null = null;

    try {
        // Create the API instance
        const api = new CertificationAggregatorApi(udemyApi, fetcher);

        // Fetch generic component data (works without slug)
        componentData = await fetchGenericCertificationAggregatorComponentData({
            api,
            locale,
        });
    } catch (error) {
        // Log the error but don't fail the entire page if aggregator data fails
        // eslint-disable-next-line no-console
        console.error('Failed to fetch certification aggregator data (non-critical)', {
            error: error instanceof Error ? error.message : String(error),
            locale,
        });
        udSentry.captureException(error);
        // Continue - the page will render without aggregator data
    }

    // END OF CERTIFICATION AGGREGATORS DATA FETCHING

    // Get topics array first
    const topics = getTopicsForBlackFridayCyberMondaySales(locale);

    // Build Promise.all array dynamically
    const results = await Promise.all([
        getStaticAppContext({locale}),
        academyDetailsPromise,
        ...topics.map((topic) => getTopicData(topic, '', locale)),
    ]);

    // Separate staticData from topic data results
    const [staticData, academyDetailsResults, ...topicDataArray] = results;

    const academiesList = academyDetailsResults.reduce<InDemandCareer[]>(
        (acc, detailsResult, index) => {
            if (detailsResult.status === 'fulfilled') {
                const academy = academiesListForLocale[index];
                const courses = detailsResult.value;
                acc.push(buildInDemandCareer(academy, courses));
            } else {
                udSentry.captureException(detailsResult.reason);
            }
            return acc;
        },
        [],
    );

    const appContext: AppContextData = {
        ...staticData,
    };

    // Fetch bundle courses for all topic sections
    const {bundleCourses, popularTopicsResults, relatedCategoriesAndSubcategories} =
        await getBundleCoursesAndPopularTopics(topicDataArray, appContext.udData);

    // Structure topics with bundle courses
    const topicsWithBundles = topicDataArray
        .map((topicData, index) => {
            if (!topicData) return null;

            const bundleCoursesForTopic = bundleCourses[index];
            const popularTopicsResult = popularTopicsResults[index];
            const relatedCategories = relatedCategoriesAndSubcategories[index];

            return {
                ...(topicData && {topic: topicData}),
                ...(bundleCoursesForTopic && {genericTopicBundleCourses: bundleCoursesForTopic}),
                ...(popularTopicsResult?.results?.[0] && {
                    popularTopics: popularTopicsResult.results[0],
                }),
                ...(relatedCategories?.length > 0 && {
                    relatedCategoriesAndSubcategories: relatedCategories,
                }),
            };
        })
        .filter(Boolean);

    // Create topics list with name and slug
    const topicsList = topics
        .map((slug, index) => {
            const topicData = topicDataArray[index];
            if (!topicData) return null;

            return {
                name: topicData.topic.name,
                slug: slug,
            };
        })
        .filter(Boolean);

    return {
        props: {
            appContext,
            dehydratedState: dehydrate(client),
            locale,
            pageKey: 'black_friday_cyber_monday_landing_page',
            renderMode: RENDER_MODES.ISR,
            translations,
            hottestTopicsList: topicsWithBundles,
            topicsList: topicsList,
            academies: academiesList,
            certificationAggregatorData: componentData,
        },
        revalidate: FOUR_HOURS,
    };
});
export default function BlackFridayCyberMondaySalesPage(
    props: BlackFridayCyberMondaySalesPageFeatureProps,
) {
    return <BlackFridayLandingPageFeature {...props} />;
}

BlackFridayCyberMondaySalesPage.getLayout = getLayout;
